<script setup lang="ts">
import { ref, watch, computed } from 'vue'
// @ts-ignore
import { VueTelInput } from 'vue-tel-input'
import {FormContact} from "@/domains/home/services/nir/forms/components/widgets/types";
import PhoneInput from "@/components/PhoneInput/PhoneInput.vue";

const props = defineProps({
  cardinality: {
    type: String,
    required: true,
    default: "SINGLE"
  },
  active:Boolean,
  modelValue: {
    type: Array as () => FormContact[],
    default: () => []
  },
  data: {
    type: Array as () => FormContact[],
    default: () => []
  }
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: FormContact[]): void
}>()

// Single contact mode
const singleContact = ref<FormContact>({
  email: '',
  phone: ''
})

// Multiple contacts mode
const multipleContacts = ref<FormContact[]>([
  { email: '', phone: '' }
])

// Watch for single contact changes
watch(() => props.data, () => {
  if (props.cardinality === 'SINGLE') {
    singleContact.value = props.data[0]
  }
  if (props.cardinality === 'MULTIPLE') {
    multipleContacts.value = props.data
  }
}, { deep: true })

watch(singleContact, (newValue) => {
  if (props.cardinality === 'SINGLE') {
    emit('update:modelValue', [newValue])
  }
}, { deep: true })

// Watch for multiple contacts changes
watch(multipleContacts, (newValue) => {
  if (props.cardinality === 'MULTIPLE') {
    emit('update:modelValue', newValue)
  }
}, { deep: true })

function addContact() {
  multipleContacts.value.push({ email: '', phone: '' })
}

function removeContact(index: number) {
  if (multipleContacts.value.length > 1) {
    multipleContacts.value.splice(index, 1)
  }
}
</script>

<template>
<div class="flex flex-col">
  <!-- SINGLE Mode -->
  <div v-if="cardinality === 'SINGLE'" class="grid grid-cols-2 gap-2">
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1.5">
        <i class="fa-solid fa-envelope text-gray-400 mr-1"></i>
        Email
      </label>
      <input
        v-model="singleContact.email"
        :disabled="!active"
        type="email"
        placeholder="Enter email address"
        class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
      />
    </div>

    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1.5">
        <i class="fa-solid fa-phone text-gray-400 mr-1"></i>
        Phone
      </label>
      <PhoneInput
          v-model="singleContact.phone"
          label="Phone"
          defaultCountry="+256"
          placeholder="Enter phone number"
          :required="true"
          size="sm"
      />
    </div>
  </div>

  <!-- MULTIPLE Mode -->
  <div v-else-if="cardinality === 'MULTIPLE'" class="space-y-3">
    <div
      v-for="(contact, index) in multipleContacts"
      :key="index"
      class="border border-gray-200 rounded p-3">
      <div class="flex items-start justify-between mb-2">
        <span class="text-xs font-semibold text-gray-600 uppercase">
          Contact {{ index + 1 }}
        </span>
        <button
          v-if="multipleContacts.length > 1"
          type="button"
          @click="removeContact(index)"
          class="btn-sm-danger"
          title="Remove contact"
        >
          <i class="fa-solid fa-trash"></i>
        </button>
      </div>

      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">
            <i class="fa-solid fa-envelope text-gray-400 mr-1"></i>
            Email
          </label>
          <input
            v-model="contact.email"
            :disabled="!active"
            type="email"
            placeholder="Enter email address"
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>

        <div>
          <PhoneInput
              v-model="contact.phone"
              label="Phone"
              defaultCountry="+256"
              placeholder="Enter phone number"
              :required="true"
              size="sm"
          />
        </div>
      </div>
    </div>

    <button
        v-if="active"
      type="button"
      @click="addContact"
      class="btn-dashed">
      <i class="fa-solid fa-plus"></i>
      <span>Add Another Contact</span>
    </button>
  </div>
</div>
</template>

<style scoped></style>
